import { useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { posthog } from '@/observability/posthog';

export interface UseSourceFailoverArgs {
  hls: Hls | null;
  videoRef: React.RefObject<HTMLVideoElement>;
  currentSource: string | null;
  alternates: string[];
  buildUrl(source: string): string;
  prefetch(source: string): Promise<void>;
  mediaKind: 'live' | 'vod';
  thresholds?: { bufferSeconds?: number; segment4xx?: number; windowMs?: number };
  /**
   * Fired synchronously, immediately before `hls.loadSource(altUrl)`. Wire this
   * to `useLobbyPlayerSync`'s returned `suppress(durationMs)` so the synthetic
   * pause/seek/play events hls.js emits during the manifest reload are not
   * broadcast to lobby peers as if the viewer initiated them.
   */
  onSwap?(altSource: string): void;
}

const DEFAULTS = { bufferSeconds: 5, segment4xx: 3, windowMs: 30_000 };

export function useSourceFailover(args: UseSourceFailoverArgs): void {
  const stateRef = useRef<{
    ailing: boolean;
    errors: number[];
    fatalAt: number | null;
    exhaustedFired: boolean;
  }>({
    ailing: false,
    errors: [],
    fatalAt: null,
    exhaustedFired: false,
  });

  // Stable ref so event callbacks always see current args without re-registering.
  const argsRef = useRef(args);
  argsRef.current = args;

  useEffect(() => {
    if (!args.hls) return undefined;
    // Reset per-source state on every effect run (new hls instance or new source).
    stateRef.current = { ailing: false, errors: [], fatalAt: null, exhaustedFired: false };
    const hls = args.hls;
    const t = { ...DEFAULTS, ...args.thresholds };

    // Track which alternates have been warmed (altName → timestamp of successful prefetch)
    const warmAlternates = new Map<string, number>();

    async function warmAlt(alt: string): Promise<void> {
      try {
        await argsRef.current.prefetch(alt);
        warmAlternates.set(alt, Date.now());
      } catch { /* best-effort */ }
    }

    function arm(reason: 'fatal' | 'rate'): void {
      const a = argsRef.current;
      try {
        posthog.captureEvent('stream_failover_armed', {
          source: a.currentSource,
          mediaKind: a.mediaKind,
          alternates: a.alternates,
          reason,
        });
      } catch { /* best-effort */ }
      a.alternates.forEach((alt) => { void warmAlt(alt); });
    }

    function onError(_e: unknown, data: {
      fatal?: boolean;
      type?: string;
      details?: string;
      response?: { code?: number };
    }): void {
      if (data?.fatal) {
        stateRef.current.fatalAt = Date.now();
        if (!stateRef.current.ailing) {
          stateRef.current.ailing = true;
          arm('fatal');
        }
        return;
      }
      // Non-fatal: check if it's a segment 4xx (ErrorDetails.FRAG_LOAD_ERROR path)
      const isFragErr = data?.details === 'fragLoadError';
      const status = data?.response?.code ?? 0;
      if (!isFragErr || status < 400 || status >= 500) return;
      const now = Date.now();
      const s = stateRef.current;
      s.errors = [...s.errors.filter((at) => now - at < t.windowMs), now];
      if (!s.ailing && s.errors.length >= t.segment4xx) {
        s.ailing = true;
        arm('rate');
      }
    }

    const interval = setInterval(() => {
      const s = stateRef.current;
      if (!s.ailing) return;
      const a = argsRef.current;
      const v = a.videoRef.current;
      if (!v) return;
      const aheadSec =
        v.buffered.length > 0 ? v.buffered.end(v.buffered.length - 1) - v.currentTime : 0;
      if (aheadSec > t.bufferSeconds) return;
      const warmAltName = a.alternates.find((alt) => {
        const at = warmAlternates.get(alt);
        return at != null && Date.now() - at < 5 * 60_000;
      });
      if (!warmAltName) {
        if (!s.exhaustedFired) {
          s.exhaustedFired = true;
          try {
            posthog.captureEvent('stream_failover_exhausted', {
              source: a.currentSource,
              mediaKind: a.mediaKind,
              alternates: a.alternates,
            });
          } catch { /* best-effort */ }
        }
        return;
      }
      const altUrl = a.buildUrl(warmAltName);
      try {
        posthog.captureEvent('stream_failover_swap', {
          from: a.currentSource,
          to: warmAltName,
          mediaKind: a.mediaKind,
          bufferAheadSec: aheadSec,
        });
      } catch { /* best-effort */ }
      // Preserve playback position for VOD streams so the swap is seamless.
      const savedTime = a.mediaKind === 'vod' && a.videoRef.current
        ? a.videoRef.current.currentTime
        : 0;
      // Fire BEFORE loadSource so callers (lobby suppress) arm their window
      // before hls.js starts emitting native video events for the reload.
      try { a.onSwap?.(warmAltName); } catch { /* best-effort */ }
      // Attach the MANIFEST_PARSED restore listener BEFORE calling
      // hls.loadSource(). When P2PML's loader is active, the new master
      // manifest can be served from peer cache or from the resolve cache
      // synchronously, dispatching MANIFEST_PARSED on the same tick that
      // loadSource() returns. Attaching the listener afterward (the
      // pre-P2P behavior) silently dropped the restore — the playhead
      // stayed at 0 with the new source's buffer pre-filled. The race was
      // invisible until P2PML made manifests arrive instantly.
      //
      // To also guard against the OLD source's MANIFEST_PARSED firing one
      // last time (it shouldn't post-loadSource, but defensive), we tag
      // the listener with the alt URL and only fire when hls reports the
      // matching `url` in its live state.
      if (a.mediaKind === 'vod' && savedTime > 0) {
        const restore = (): void => {
          // Guard: only restore once the NEW source is actually loaded.
          // hls.js's `Hls#url` reflects the current source URL.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const liveUrl = (hls as unknown as { url?: string }).url ?? '';
          if (liveUrl && !liveUrl.endsWith(altUrl) && !altUrl.endsWith(liveUrl)) {
            // Stale MANIFEST_PARSED from the old source — ignore.
            return;
          }
          const vEl = a.videoRef.current;
          if (vEl) {
            vEl.currentTime = savedTime;
            vEl.play().catch(() => { /* user gesture may be required; harmless */ });
          }
          hls.off(Hls.Events.MANIFEST_PARSED, restore);
        };
        hls.on(Hls.Events.MANIFEST_PARSED, restore);
      }
      hls.loadSource(altUrl);
      warmAlternates.delete(warmAltName);                // force re-warm next time
      warmAlternates.set(a.currentSource ?? '', 0);      // expire the old source (0 < now - 5min)
      s.ailing = false;
      s.errors = [];
      s.fatalAt = null;
      s.exhaustedFired = false;
    }, 1000);

    function onFragLoaded(): void {
      const s = stateRef.current;
      const wasAiling = s.ailing;
      s.errors = [];
      s.exhaustedFired = false;
      if (wasAiling) {
        s.ailing = false;
        const a = argsRef.current;
        try {
          posthog.captureEvent('stream_failover_recovered', {
            source: a.currentSource,
            mediaKind: a.mediaKind,
          });
        } catch { /* best-effort */ }
      }
    }

    // hls.on/off are typed against HlsListeners; cast to any for the broader data shape.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const hlsAny = hls as any;
    hlsAny.on(Hls.Events.ERROR, onError);
    hlsAny.on(Hls.Events.FRAG_LOADED, onFragLoaded);
    return (): void => {
      hlsAny.off(Hls.Events.ERROR, onError);
      hlsAny.off(Hls.Events.FRAG_LOADED, onFragLoaded);
      clearInterval(interval);
    };
  }, [args.hls, args.currentSource]); // eslint-disable-line react-hooks/exhaustive-deps
}
